/**
 * A regra da Matriz de Produtos (Serviços), em código puro: o porte de `carregarMatrizServicos`
 * (`portal/admin.html:3631-3663`) na parte que não é desenho.
 *
 * Uma linha por cliente, uma coluna por serviço, e a coluna "Lacunas" com quantos serviços ela
 * ainda não contratou. Ordem: mais lacunas primeiro (maior potencial de venda no topo), e empate
 * pelo nome, com `localeCompare` SEM idioma, exatamente como o legado (`admin.html:3647`).
 */

// Os oito serviços, na ordem das colunas: a mesma chave que o lead grava.
import { PRODUTOS_LEAD } from '../vendas/constantes.js';
// A cliente e os produtos dela, como a junção da lista os monta.
import type { ClienteDaLista, ListaDeClientes, ProdutosDaCliente } from '../clientes/regras.js';

// Os rótulos curtos do cabeçalho (`admin.html:3618-3627`). O nome completo vem de `PRODUTOS_LEAD`.
export const PRODUTOS_LEAD_ABREVIADO: Readonly<Record<string, string>> = Object.freeze({
  eip: 'EIP',
  analise_proporcao_3d: 'Prop. 3D',
  coloracao_pessoal: 'Coloração',
  montagem_looks: 'Looks',
  mala_viagem: 'Mala Viagem',
  personal_shopper: 'Personal Shopper',
  acompanhamento_anual: 'Acomp. Anual',
  projetos_sob_medida: 'Sob Medida',
});

// As chaves na ordem das colunas (`Object.keys(PRODUTOS_LEAD)`, `admin.html:3640`).
export const CHAVES_DA_MATRIZ: readonly string[] = Object.freeze(Object.keys(PRODUTOS_LEAD));

/** O que uma célula mostra: finalizado, em andamento, ou lacuna (`admin.html:3656-3658`). */
export type SituacaoDoServico = 'finalizado' | 'andamento' | 'lacuna';

// A cliente sem lead nenhum: nada contratado e nada finalizado.
const SEM_PRODUTO: ProdutosDaCliente = Object.freeze({ contratados: [], finalizados: [] });

/** A situação de um serviço para uma cliente. Finalizado vence em andamento, como no legado. */
export function situacaoDoServico(produtos: ProdutosDaCliente, chave: string): SituacaoDoServico {
  if (produtos.finalizados.includes(chave)) return 'finalizado';
  if (produtos.contratados.includes(chave)) return 'andamento';
  return 'lacuna';
}

/** Uma linha da tabela. */
export interface LinhaDaMatriz {
  readonly cliente: ClienteDaLista;
  readonly produtos: ProdutosDaCliente;
  readonly lacunas: number;
}

/** As linhas na ordem do legado (`admin.html:3640-3647`). */
export function linhasDaMatriz(lista: ListaDeClientes): LinhaDaMatriz[] {
  return lista.clientes
    .map((cliente) => {
      const produtos = lista.produtosPorCliente.get(cliente.user_id) ?? SEM_PRODUTO;
      // Lacuna: nem contratado nem finalizado.
      const lacunas = CHAVES_DA_MATRIZ.filter((c) => !produtos.contratados.includes(c) && !produtos.finalizados.includes(c)).length;
      return { cliente, produtos, lacunas };
    })
    .sort((a, b) => b.lacunas - a.lacunas || (a.cliente.nome_completo || '').localeCompare(b.cliente.nome_completo || ''));
}

/** O nome na primeira coluna: nome, e-mail, ou "(sem nome)" (`admin.html:3660`). */
export function nomeNaMatriz(cliente: ClienteDaLista): string {
  return cliente.nome_completo || cliente.email || '(sem nome)';
}
